import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal,
    Button,
    Box,
    Text,
    Alert,
    Table,
    Paper,
    Badge,
    ActionIcon,
    Group,
    ScrollArea,
    TextInput,
    Pagination,
    Tooltip,
} from '@mantine/core';
import { IconX, IconEye, IconSearch } from '@tabler/icons-react';
import { MODAL_SIZES, TABLE_SCROLL_MIN_WIDTH } from '../../theme/mantineTheme';
import { EmptyState } from '../common/EmptyState';
import { entityService } from '../../services/entityServiceSingleton';
import { getPluralizedLabel } from '../../utils/pluralizationUtils';
import { extractErrorMessage } from '../../utils/apiUtils';
import { LoadingState } from '../common/LoadingState';
import { DialogErrorAlert } from './DialogErrorAlert';

interface FilterPreviewDialogProps {
    open: boolean;
    onClose: () => void;
    filter: string;
    filterScope: string;
    entityType?: string;
    /** When set (e.g. Group preview members), use this title instead of "Filter Preview: ...". */
    titleOverride?: string;
    /** When set (e.g. Group), show this note at the top (e.g. OnPremId requirement). */
    noteMessage?: string;
    /** When set, request these attributes from the API (e.g. ['Id', 'DisplayName', 'EntityType', 'OnPremId']). */
    attributes?: string[];
}

export const FilterPreviewDialog: React.FC<FilterPreviewDialogProps> = ({
    open,
    onClose,
    filter,
    filterScope,
    entityType,
    titleOverride,
    noteMessage,
    attributes
}) => {
    const PAGE_SIZE = 25;
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [totalResults, setTotalResults] = useState<number | null>(null);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const loadPreview = useCallback(async (pageNum: number, searchText: string) => {
        if (!filter || !filterScope) {
            setPreviewError('Filter and filter scope are required');
            return;
        }

        const filterScopeString = typeof filterScope === 'string'
            ? filterScope.trim()
            : (filterScope && typeof filterScope === 'object'
                ? String((filterScope as any).Id ?? (filterScope as any).id ?? (filterScope as any).DisplayName ?? (filterScope as any).displayName ?? '').trim()
                : String(filterScope ?? '').trim());
        if (!filterScopeString) {
            setPreviewError('Filter scope is required');
            return;
        }

        setPreviewLoading(true);
        setPreviewError(null);

        try {
            const response = await entityService.getEntitiesByType(
                filterScopeString,
                pageNum,
                PAGE_SIZE,
                undefined,
                filter,
                attributes,
                undefined,
                searchText.trim() || undefined
            );

            setPreviewData(response.data || []);
            setTotalResults(response.totalResults ?? null);
            setTotalPages(Math.max(1, response.totalPages ?? Math.ceil((response.totalResults ?? 0) / PAGE_SIZE)));
        } catch (err: any) {
            console.error('Error loading filter preview:', err);
            let errorMessage = extractErrorMessage(err);
            if (err?.response?.status === 400) {
                errorMessage = `Invalid filter: ${errorMessage}. The filter expression may be malformed or the attribute may not exist.`;
            }
            setPreviewError(errorMessage);
            setPreviewData([]);
            setTotalResults(null);
            setTotalPages(1);
        } finally {
            setPreviewLoading(false);
        }
    }, [filter, filterScope, attributes, entityService]);

    // When dialog closes, reset so next open starts at page 1 with no search
    useEffect(() => {
        if (!open) {
            setPreviewData([]);
            setPreviewError(null);
            setTotalResults(null);
            setTotalPages(1);
            setPage(1);
            setSearchInput('');
            setAppliedSearch('');
        }
    }, [open]);

    // When dialog is open and filter/scope are set, load current page (and refetch when page or appliedSearch change)
    useEffect(() => {
        if (open && filter && filterScope) {
            loadPreview(page, appliedSearch);
        }
    }, [open, filter, filterScope, page, appliedSearch, loadPreview]);

    // Ensure we have a string for the entity type
    const displayEntityType = entityType || filterScope || '';
    const pluralLabel = typeof displayEntityType === 'string' && displayEntityType ? getPluralizedLabel(displayEntityType) : 'Entities';

    return (
        <Modal
            opened={open}
            onClose={onClose}
            size={MODAL_SIZES.detail}
            withCloseButton={false}
            title={
                <Tooltip label="FilterPreviewDialog" withArrow position="top">
                    <span style={{ cursor: 'help' }}>
                        <Group justify="space-between" flex={1} w="100%" component="span">
                            <Group gap="xs" component="span">
                                <IconEye size={20} />
                                <Text fw={600} component="span">{titleOverride ?? `Filter Preview: ${pluralLabel}`}</Text>
                            </Group>
                            <ActionIcon
                        aria-label="close"
                        onClick={onClose}
                        variant="subtle"
                        color="gray"
                    >
                        <IconX size={18} />
                    </ActionIcon>
                        </Group>
                    </span>
                </Tooltip>
            }
        >
            {noteMessage && (
                <Alert color="blue" mb="md" title="Note">
                    {noteMessage}
                </Alert>
            )}
            <Box mb="md">
                <Text size="sm" c="dimmed" mb="xs">
                    <strong>Filter:</strong>
                </Text>
                <Paper withBorder p="sm" mt="xs">
                    <Text size="sm" ff="monospace">
                        {filter}
                    </Text>
                </Paper>
            </Box>

            <Group gap="xs" mb="md">
                <TextInput
                    placeholder="Search (e.g. name)"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            setAppliedSearch(searchInput);
                            setPage(1);
                        }
                    }}
                    leftSection={<IconSearch size={16} />}
                    flex={1}
                />
                <Button
                    variant="light"
                    onClick={() => {
                        setAppliedSearch(searchInput);
                        setPage(1);
                    }}
                >
                    Search
                </Button>
            </Group>

            <DialogErrorAlert error={previewError} />

            {previewLoading ? (
                <LoadingState variant="centered" minHeight={200} />
            ) : previewData.length > 0 ? (
                <>
                    <ScrollArea h={400}>
                        <Table.ScrollContainer minWidth={TABLE_SCROLL_MIN_WIDTH}>
                            <Table striped highlightOnHover stickyHeader>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th><strong>ID</strong></Table.Th>
                                        <Table.Th><strong>Display Name</strong></Table.Th>
                                        {previewData[0]?.EntityType && (
                                            <Table.Th><strong>Entity Type</strong></Table.Th>
                                        )}
                                        {attributes?.includes('OnPremId') && (
                                            <Table.Th><strong>OnPremId</strong></Table.Th>
                                        )}
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {previewData.map((entity, index) => (
                                        <Table.Tr key={entity.Id || entity.id || index}>
                                            <Table.Td ff="monospace" fz="sm">
                                                {entity.Id || entity.id || 'N/A'}
                                            </Table.Td>
                                            <Table.Td>
                                                {entity.DisplayName || entity.displayName || 'N/A'}
                                            </Table.Td>
                                            {entity.EntityType && (
                                                <Table.Td>
                                                    <Badge size="sm">{entity.EntityType}</Badge>
                                                </Table.Td>
                                            )}
                                            {attributes?.includes('OnPremId') && (
                                                <Table.Td ff="monospace" fz="sm">
                                                    {entity.OnPremId ?? entity.onPremId ?? '—'}
                                                </Table.Td>
                                            )}
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </Table.ScrollContainer>
                    </ScrollArea>
                    {totalPages > 1 && (
                        <Pagination
                            total={totalPages}
                            value={page}
                            onChange={setPage}
                            disabled={previewLoading}
                            mt="md"
                            size="sm"
                        />
                    )}
                    <Text size="sm" c="dimmed" mt="md" component="div">
                        {totalResults !== null ? (
                            <>
                                Showing <strong>{(page - 1) * PAGE_SIZE + 1}</strong>–<strong>{(page - 1) * PAGE_SIZE + previewData.length}</strong> of <strong>{totalResults.toLocaleString()}</strong> matching {pluralLabel.toLowerCase()}.
                                {totalResults > PAGE_SIZE && ' Use pagination to browse all pages.'}
                            </>
                        ) : (
                            `Showing page ${page} (${previewData.length} on this page).`
                        )}
                    </Text>
                </>
            ) : (
                <EmptyState title="No entities match this filter" />
            )}

            <Group justify="flex-end" mt="lg">
                <Button onClick={onClose} variant="default" leftSection={<IconX size={16} />}>
                    Close
                </Button>
            </Group>
        </Modal>
    );
};
